import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";

const faqGroups = [
  {
    group: "阅读功能",
    items: [
      {
        q: "策驰漫画网支持哪些阅读方式?",
        a: "支持流畅的翻页阅读和目录快速跳转。你可以逐页翻阅,也能从目录一键跳到任意章节,还能通过键盘方向键快捷翻页,阅读体验更顺滑。",
      },
      {
        q: "目录跳转和章节选择怎么操作?",
        a: "在阅读页右上角点击「目录」按钮即可打开章节目录,点击任意章节即可跳转;在漫画详情页也能直接选择章节进入阅读。",
      },
      {
        q: "可以调节阅读页的字体和显示吗?",
        a: "阅读页默认提供清晰的版式与舒适排版,并支持夜间模式切换,夜间模式会在暗光环境下显著降低光线刺激,保护眼睛。",
      },
    ],
  },
  {
    group: "收藏与追更",
    items: [
      {
        q: "如何收藏喜欢的漫画?",
        a: "在漫画详情页或阅读页点击「收藏」按钮即可把作品加入书架。收藏后,作品更新时你会在收藏列表第一时间看到最新章节提醒,方便追更。",
      },
      {
        q: "收藏的漫画会丢失吗?",
        a: "不会。收藏与阅读记录都会自动保存并同步,即便更换设备,登录后你的书架和进度都会保持一致。",
      },
      {
        q: "如何查看已经收藏和读过的作品?",
        a: "你的书架会集中展示已收藏的作品,阅读记录则记录了最近读过的章节和进度,点击即可从上次的位置继续阅读。",
      },
    ],
  },
  {
    group: "阅读记录与同步",
    items: [
      {
        q: "阅读记录会自动保存吗?",
        a: "会自动保存。每次翻页、切换章节时,系统都会记录你的阅读进度,回到漫画详情页时会有「继续阅读」入口,直接接着上次的位置看。",
      },
      {
        q: "更换设备后阅读进度还在吗?",
        a: "在的。阅读记录会自动同步,只要使用同一账号登录,在手机、平板或电脑上切换,进度都不会丢失。",
      },
      {
        q: "夜间模式开启后能记住我的选择吗?",
        a: "能。夜间模式的开关会保留你的偏好,下次打开网站时仍会沿用你上次的选择,无需重复设置。",
      },
    ],
  },
  {
    group: "使用与反馈",
    items: [
      {
        q: "遇到页面加载或翻页异常怎么办?",
        a: "建议先刷新页面或清除浏览器缓存后重试。如果问题仍然存在,可通过「关于我们」页面的联系方式反馈,我们会尽快处理。",
      },
      {
        q: "可以反馈漫画或建议新功能吗?",
        a: "当然可以。我们非常欢迎读者的建议,无论是想看的新作品、希望优化的功能,还是阅读体验上的改进意见,都可以随时告诉我们。",
      },
    ],
  },
];

export default function Faq() {
  const [open, setOpen] = useState<string | null>(null);

  const allItems = faqGroups.flatMap((g) => g.items);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">常见问题</span>
        </nav>

        <header className="mt-4">
          <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">常见问题</h1>
          <p className="mt-2 text-sm text-foreground-600">关于阅读、收藏、追更与记录同步,你想知道的都在这里</p>
        </header>

        <div className="mt-8 flex flex-col gap-8">
          {faqGroups.map((group) => (
            <section key={group.group}>
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                <i className="ri-price-tag-3-line text-accent-500"></i>
                {group.group}
              </h2>
              <div className="mt-3 flex flex-col gap-3">
                {group.items.map((item) => {
                  const key = `${group.group}-${item.q}`;
                  const isOpen = open === key;
                  return (
                    <div key={key} className="rounded-xl border border-background-200/80 bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? null : key)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                      >
                        <span className="text-sm font-bold text-foreground-950">{item.q}</span>
                        <i
                          className={`shrink-0 text-lg text-primary-600 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          } ri-add-line`}
                        ></i>
                      </button>
                      {isOpen && (
                        <p className="border-t border-background-200/70 px-5 py-4 text-sm leading-6 text-foreground-600">
                          {item.a}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-primary-700 px-6 py-8 text-center">
          <h2 className="font-heading text-xl text-background-50">没找到你要的答案?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-background-100/90">
            可以前往「关于我们」页面向我们反馈,或联系官方渠道,我们会尽快为你解答。
          </p>
          <Link
            to="/about"
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm font-bold text-primary-800 hover:bg-accent-500 hover:text-background-50"
          >
            联系我们
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      {/* FAQPage schema - matches visible Q&A */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </Layout>
  );
}